import { Cache } from 'utils/cache';
import { user } from 'utils/auth/user';
import { CustomerFacingError } from 'utils/ui/errors';
import { EErrorCode } from 'config/errors-codes';
import { IBrand, IMarket } from 'data/schema';

const cache = new Cache();

type TLegalApiResponse<T = Record<string, unknown>> = {
  data: T;
  error: Record<string, unknown>;
  statusCode: number;
  success: boolean;
};

type TermsOfUseData = {
  acceptedTimestamp: string;
  docUrl: string;
  isAccepted: boolean;
  version: string;
};

type UserType = 'dealer' | 'customer';

export type LegalDocuments = {
  termsOfUse?: { version: number; url: string } | string;
  privacyPolicy?: string;
  imprint?: string;
};

type LegalDocumentsByUser = {
  [K in UserType]: LegalDocuments;
};

export async function checkTermsOfUse(brand: IBrand, market: IMarket, locale: string): Promise<TermsOfUseData> {
  let response;

  response = cache.getValue<TermsOfUseData>(`tou-${brand}-${market}-${locale}`);
  if (response) {
    return response;
  }

  try {
    response = await (
      await fetch(
        new URL(
          `${process.env.REACT_APP_LEGALAPI}/check?activeBrand=${brand}&activeMarket=${market}&activeLanguage=${locale}`
        ),
        { headers: { Authorization: user.getIdToken()! } }
      )
    ).json();
  } catch (err) {
    console.error('Failed to get the terms of use.', err);
    throw new CustomerFacingError(EErrorCode.TERMS_OF_USE);
  }

  if (response.error && Object.keys(response.error).length !== 0) {
    console.error('Failed to get the terms of use.', response.error);
    throw new CustomerFacingError(EErrorCode.TERMS_OF_USE);
  }

  cache.setValue(`tou-${brand}-${market}-${locale}`, response.data);

  return response.data;
}

export async function acceptTermsOfUse(brand: IBrand, market: IMarket, locale: string): Promise<TermsOfUseData> {
  let response: TLegalApiResponse<TermsOfUseData>;
  try {
    response = await (
      await fetch(
        `${process.env.REACT_APP_LEGALAPI}/accept?activeBrand=${brand}&activeMarket=${market}&activeLanguage=${locale}`,
        {
          method: 'POST',
          headers: { Authorization: user.getIdToken()! },
        }
      )
    ).json();
  } catch (error) {
    throw new Error(`Unable to accept Terms of Use: ${(error as Error).message}`);
  }

  if (response.error && Object.keys(response.error).length !== 0) {
    console.error('Failed to accept the terms of use.', response.error);
    throw new Error(`Unable to accept Terms of Use: ${response.error.message}`);
  }

  cache.setValue(`tou-${brand}-${market}-${locale}`, response.data);

  return response.data;
}

export async function getLegalDocuments(brand: string, market: string, locale: string): Promise<LegalDocumentsByUser> {
  let response;

  response = cache.getValue<LegalDocumentsByUser>(`legal-${brand}-${market}-${locale}`);
  if (response) {
    return response;
  }

  try {
    response = await (
      await fetch(
        new URL(
          `${process.env.REACT_APP_LEGALAPI}/legal?activeBrand=${brand}&activeMarket=${market}&activeLanguage=${locale}`
        ),
        { headers: { Authorization: user.getIdToken()! } }
      )
    ).json();
  } catch (err) {
    console.error('Failed to get the legal documents.', err);
    throw new CustomerFacingError(EErrorCode.LEGAL_DOCS);
  }

  if (response.error && Object.keys(response.error).length !== 0) {
    console.error('Failed to get the legal documents.', response.error);
    throw new CustomerFacingError(EErrorCode.LEGAL_DOCS);
  }

  /** Group documents by user */
  response = {
    dealer: {
      termsOfUse: response.data.termsOfUse,
      privacyPolicy: response.data.privacyPolicy,
      imprint: response.data.imprint,
    },
    customer: {
      termsOfUse: response.data.b2cTermsOfUse,
      privacyPolicy: response.data.b2cPrivacyPolicy,
      imprint: response.data.imprint,
    },
  };

  cache.setValue(`legal-${brand}-${market}-${locale}`, response);

  return response;
}
